import { addToolBreadcrumb } from '@tunasong/models'
import { computeSessionStats, generateQuizQuestion, validateAnswer } from '@tunasong/music-lib'
import type { AnswerVerdict, FunctionCategory, QuizQuestion } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import { Button, useSearchParams } from '@tunasong/ui-lib'
import { useEffect, useState } from 'react'
import type { FC } from 'react'
import { RelatedTools, type RelatedTool } from '../related-tools/related-tools.js'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import { FunctionQuizFeedback } from './function-quiz-feedback.js'
import { parseForcedScale } from './function-quiz-params.js'
import { useFunctionQuizPlayback } from './function-quiz-playback.js'
import { FunctionQuizQuestion } from './function-quiz-question.js'
import { FunctionQuizSummary } from './function-quiz-summary.js'
import styles from './function-quiz.module.css'

const QUESTIONS_PER_SESSION = 10
const AUTOPLAY_DELAY_MS = 250

interface SessionResult {
  question: QuizQuestion
  verdict: AnswerVerdict
}

type SessionState =
  | { phase: 'welcome' }
  | { phase: 'asking'; index: number; question: QuizQuestion; results: SessionResult[] }
  | { phase: 'feedback'; index: number; question: QuizQuestion; verdict: AnswerVerdict; results: SessionResult[] }
  | { phase: 'done'; results: SessionResult[] }

/**
 * Function Quiz v1 host. State machine: welcome → asking → feedback → done.
 * Owns the session state, generates new questions per round, validates the
 * user's answer via the analyzer, and stitches the panels together.
 *
 * Audition autoplay: when a new question mounts (phase becomes `asking`),
 * the audition plays after a short delay (lets the panel render first).
 * Manual `▶ Hear it` / `▶ Hear it again` triggers a fresh playback.
 *
 * No persistence — sessions live in component state only (spec §2 Q6).
 *
 * Spec: docs/superpowers/specs/2026-05-18-function-quiz-design.md §4
 */
export const FunctionQuiz: FC = () => {
  const [searchParams] = useSearchParams()
  const [forcedScale] = useState<Scale | null>(() =>
    parseForcedScale(searchParams.get('key'), searchParams.get('keyType'))
  )
  const [state, setState] = useState<SessionState>({ phase: 'welcome' })
  const welcomeIntro = useTheoryCopy('function-quiz-welcome-intro')
  const activeQuestion = state.phase === 'asking' || state.phase === 'feedback' ? state.question : null

  // Polish 6/6 — tool telemetry. Open-event breadcrumb on mount.
  useEffect(() => {
    addToolBreadcrumb('opened', { tool: 'function-quiz' })
  }, [])
  const { play } = useFunctionQuizPlayback({
    quizChord: activeQuestion?.chord ?? null,
    tonicChord: activeQuestion?.tonicChord ?? null,
  })

  // Autoplay the audition shortly after a new question mounts (Bowie §8.4).
  // Effect dep depends on the question identity, not the phase string, so
  // moving asking → feedback → asking (next question) re-triggers autoplay.
  const askingQuestion = state.phase === 'asking' ? state.question : null
  useEffect(() => {
    if (!askingQuestion) {
      return
    }
    const handle = window.setTimeout(play, AUTOPLAY_DELAY_MS)
    return () => window.clearTimeout(handle)
  }, [askingQuestion, play])

  const generate = (): QuizQuestion =>
    forcedScale !== null ? generateQuizQuestion(Math.random, { forcedScale }) : generateQuizQuestion()

  const start = () => {
    setState({ phase: 'asking', index: 0, question: generate(), results: [] })
  }

  const answer = (picked: FunctionCategory) => {
    if (state.phase !== 'asking') {
      return
    }
    const verdict = validateAnswer(state.question, picked)
    setState({ phase: 'feedback', index: state.index, question: state.question, verdict, results: state.results })
  }

  const next = () => {
    if (state.phase !== 'feedback') {
      return
    }
    const results = [...state.results, { question: state.question, verdict: state.verdict }]
    if (state.index + 1 >= QUESTIONS_PER_SESSION) {
      setState({ phase: 'done', results })
      return
    }
    setState({
      phase: 'asking',
      index: state.index + 1,
      question: generate(),
      results,
    })
  }

  // Footer is shown on welcome + done phases only (Bowie Q2: undistracted
  // quiz UX during asking/feedback). On welcome, defaults are D Dorian for
  // Mode Lab and C major for Reharm Lab; on done, propagate the last
  // question's scale (and the last *major-key* scale for Reharm Lab).
  const buildFooterTools = (): RelatedTool[] => {
    if (state.phase === 'done' && state.results.length > 0) {
      const last = state.results[state.results.length - 1].question.scale
      const modeTonic = last.root
      // Reharm Lab v1 is major-only — find the last major-key question's
      // scale; fall back to C if none.
      const lastMajor =
        [...state.results].reverse().find(r => r.question.scale.type === 'major')?.question.scale.root ?? 'C'
      return [
        { targetRoute: `/app/modes?tonic=${modeTonic}`, lexiconId: 'crosslink-quiz-to-mode' },
        { targetRoute: `/app/reharm?key=${lastMajor}`, lexiconId: 'crosslink-quiz-to-reharm' },
        { targetRoute: '/app/ear-training', lexiconId: 'crosslink-quiz-to-ear' },
      ]
    }
    return [
      { targetRoute: '/app/modes?tonic=D', lexiconId: 'crosslink-quiz-to-mode' },
      { targetRoute: '/app/reharm?key=C', lexiconId: 'crosslink-quiz-to-reharm' },
      { targetRoute: '/app/ear-training', lexiconId: 'crosslink-quiz-to-ear' },
    ]
  }
  const footerTools = buildFooterTools()

  if (state.phase === 'welcome') {
    return (
      <div className={styles.container}>
        <section className={styles.welcome}>
          {welcomeIntro?.body ? <p className={styles.intro}>{welcomeIntro.body}</p> : null}
          <p className={styles.intro}>{QUESTIONS_PER_SESSION} questions per session.</p>
          <Button variant="contained" color="primary" size="large" onClick={start}>
            Start session
          </Button>
        </section>
        <RelatedTools tools={footerTools} />
      </div>
    )
  }

  if (state.phase === 'asking') {
    const scoreCorrect = state.results.filter(r => r.verdict.correct).length
    return (
      <div className={styles.container}>
        <FunctionQuizQuestion
          question={state.question}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length}
          interactive={true}
          onHearIt={play}
          onAnswer={answer}
        />
      </div>
    )
  }

  if (state.phase === 'feedback') {
    const scoreCorrect = state.results.filter(r => r.verdict.correct).length + (state.verdict.correct ? 1 : 0)
    return (
      <div className={styles.container}>
        <FunctionQuizQuestion
          question={state.question}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length + 1}
          interactive={false}
          onHearIt={play}
          onAnswer={() => {
            /* disabled in feedback phase via interactive={false} */
          }}
        />
        <FunctionQuizFeedback question={state.question} verdict={state.verdict} onHearAgain={play} onNext={next} />
      </div>
    )
  }

  // state.phase === 'done'
  return (
    <div className={styles.container}>
      <FunctionQuizSummary stats={computeSessionStats(state.results)} onRestart={start} />
      <RelatedTools tools={footerTools} />
    </div>
  )
}
